import { useCallback, useRef, useState } from "react";

interface UsePushToTalkOptions {
  enabled: boolean;
  onPressStart: () => void;
  onPressEnd: () => void;
  disabled?: boolean;
}

export const usePushToTalk = ({
  enabled,
  onPressStart,
  onPressEnd,
  disabled = false,
}: UsePushToTalkOptions) => {
  const [isPressed, setIsPressed] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const isPressedRef = useRef(false);
  const activePointerIdRef = useRef<number | null>(null);

  const endPress = useCallback(
    (pointerId?: number) => {
      if (!isPressedRef.current) return;
      if (
        pointerId != null &&
        activePointerIdRef.current != null &&
        pointerId !== activePointerIdRef.current
      ) {
        return;
      }

      isPressedRef.current = false;
      activePointerIdRef.current = null;
      setIsPressed(false);

      if (pointerId != null) {
        try {
          if (buttonRef.current?.hasPointerCapture(pointerId)) {
            buttonRef.current.releasePointerCapture(pointerId);
          }
        } catch {
          // ignore if capture was already released
        }
      }

      onPressEnd();
    },
    [onPressEnd],
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (!enabled || disabled || isPressedRef.current) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;

      event.preventDefault();
      event.stopPropagation();

      activePointerIdRef.current = event.pointerId;
      isPressedRef.current = true;
      setIsPressed(true);

      // Keep receiving pointerup/move even if the finger slides off the button.
      try {
        (event.currentTarget as HTMLButtonElement).setPointerCapture(
          event.pointerId,
        );
      } catch {
        // Some environments reject capture; document-level up still helps below.
      }
      buttonRef.current = event.currentTarget;

      onPressStart();
    },
    [disabled, enabled, onPressStart],
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (!enabled) return;
      endPress(event.pointerId);
    },
    [enabled, endPress],
  );

  // Genuine OS/browser cancel (call interrupt, gesture takeover, etc.) —
  // NOT finger sliding off the button (that is pointerleave, which we ignore).
  const handlePointerCancel = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (!enabled) return;
      endPress(event.pointerId);
    },
    [enabled, endPress],
  );

  return {
    buttonRef,
    isPressed,
    isPushToTalk: enabled,
    pushToTalkHandlers: enabled
      ? {
          onPointerDown: handlePointerDown,
          onPointerUp: handlePointerUp,
          onPointerCancel: handlePointerCancel,
        }
      : {},
  };
};
